/**
 * @file Swipeable.tsx
 * @description Provides swipe gesture context for coordinating swipeable list items.
 * Ensures only one item can be swiped open at a time across the app.
 * @module providers/Swipeable
 */
import type { ReactNode, RefObject } from "react";
import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { Swipeable } from "react-native-gesture-handler";

/**
 * Context value for managing swipeable gesture coordination
 * @remarks Tracks the previously opened swipeable to auto-close it when a new one opens
 */
interface SwipeableProviderContext {
  previous: RefObject<Swipeable | null> | null;
  setOpen: (ref: RefObject<Swipeable | null> | undefined) => void;
}

export const SwipeableContext = createContext<SwipeableProviderContext>({
  previous: null,
  setOpen: () => {},
});

/**
 * Props for the SwipeableProvider component
 */
interface SwipeableProviderProps {
  readonly children?: ReactNode;
}
/**
 * Provider component that manages swipeable gesture state across the application.
 * @param root0 - Component props
 * @param root0.children - React children nodes to render within the provider
 */
export function SwipeableProvider({ children }: SwipeableProviderProps) {
  const previousRef = useRef<Swipeable | null>(null);

  const handleSetOpen = useCallback(
    (ref: RefObject<Swipeable | null> | undefined) => {
      if (previousRef.current && previousRef.current !== ref?.current) {
        previousRef.current?.close();
      }
      previousRef.current = ref?.current ?? null;
    },
    []
  );

  const value = useMemo(() => {
    return {
      previous: previousRef,
      setOpen: handleSetOpen,
    };
  }, [handleSetOpen]);

  return (
    <SwipeableContext.Provider value={value}>
      {children}
    </SwipeableContext.Provider>
  );
}

/**
 * Hook to access the swipeable context for coordinating swipe gestures
 * @returns The swipeable context containing the previous ref and setOpen callback
 * @remarks Use this hook in swipeable list items to register when they open
 */
export function useSwipeableProvider() {
  return useContext(SwipeableContext);
}
